import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import { FaAd, FaPaperPlane, FaSpinner, FaRedo, FaInbox } from 'react-icons/fa';
import AdsSubNav from '../../Common/AdsSubNav';

const RejectedLeads = () => {
  const navigate = useNavigate();
  const [rejectedLeads, setRejectedLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 10;
  const totalPages = Math.ceil(rejectedLeads.length / rowsPerPage);

  // Get current page data
  const paginatedLeads = rejectedLeads.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  // Handle page change
  const handlePageChange = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };


  const stats = {
    rejectedAds: rejectedLeads.length,
  };



  const fetchRejectedLeads = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_BASE_URL}/get/rejected/ads`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      console.log('Rejected Leads Response:', response.data);

      const rejectedAds = response.data.data || [];
      console.log('Rejected ads count:', rejectedAds.length);

      setRejectedLeads(rejectedAds);
      setError(null);
    } catch (err) {
      console.error('Error fetching rejected advertisements:', err);
      setError('Failed to load rejected advertisements. Please try again later.');
      toast.error('Failed to load rejected advertisements');
    } finally {
      setLoading(false);
    }
  };

  // Fetch rejected advertisements for the reporter
  useEffect(() => {
    fetchRejectedLeads();
  }, []);

  return (
    <div className="p-2 sm:p-6 bg-white rounded-lg shadow-md animate-fadeIn">
      {/* Modern Header - Ultra Compact */}
      <div className="mb-4 sm:mb-5">
        <div className="bg-gradient-to-r from-rose-600 via-red-600 to-pink-600 rounded-xl sm:rounded-[1.5rem] shadow-lg p-2.5 sm:p-4 text-white">
          <div className="flex flex-row items-center justify-between gap-2 sm:gap-4">

            <div className="flex items-center gap-2 sm:gap-3">
              <button
                onClick={() => navigate(-1)}
                className="hidden sm:flex px-2.5 py-1 rounded-full bg-white/10 hover:bg-white/20 text-white text-[10px] sm:text-xs font-black uppercase tracking-wider border border-white/20 transition-all whitespace-nowrap"
              >
                ← Back
              </button>
              <div className="p-1.5 sm:p-2 bg-white/20 rounded-lg backdrop-blur-sm shadow-inner flex-shrink-0">
                <FaAd className="text-[12px] sm:text-base text-white" />
              </div>
              <div className="flex flex-col justify-center">
                <h1 className="text-xs sm:text-lg font-black tracking-tight leading-none mb-0.5 sm:mb-1">Denied Ads</h1>
                <p className="text-rose-50 text-[8px] sm:text-[10px] font-bold uppercase tracking-widest leading-none">Rejected: {stats.rejectedAds}</p>
              </div>
            </div>

            <div className="flex items-center gap-2 sm:gap-3">
              <div className="hidden sm:flex flex-col items-end pr-3 border-r border-white/20">
                <div className="text-sm sm:text-base font-black leading-none">{stats.rejectedAds}</div>
                <div className="text-[8px] sm:text-[9px] text-rose-50 uppercase tracking-widest font-bold">Requires Fix</div>
              </div>
              <button
                className="flex items-center justify-center gap-1.5 bg-white/20 hover:bg-white/30 px-3 py-1.5 rounded-lg transition-colors backdrop-blur-sm text-[10px] sm:text-xs tracking-wider font-black uppercase shadow-sm"
                onClick={fetchRejectedLeads}
                disabled={loading}
              >
                <FaSpinner className={`w-3 h-3 ${loading ? "animate-spin" : "hidden"}`} />
                <span>{loading ? "Loading..." : "Refresh"}</span>
              </button>
            </div>

          </div>
        </div>
      </div>

      <AdsSubNav type="paid" />

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <FaSpinner className="animate-spin text-yellow-500 text-4xl" />
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          <p>{error}</p>
        </div>
      )}

      {/* No Rejected Leads - Professional Neutral Empty State */}
      {!loading && !error && rejectedLeads.length === 0 && (
        <div className="max-w-xl mx-auto text-center py-16 px-4 animate-fadeIn">
          <div className="relative inline-block mb-8">
            <div className="absolute inset-0 bg-rose-400/20 blur-[50px] rounded-full" />
            <div className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-full border-4 border-rose-100 shadow-2xl overflow-hidden mx-auto bg-white flex items-center justify-center ring-8 ring-rose-50/50">
              <FaInbox className="text-rose-200 text-7xl sm:text-8xl" />
            </div>
          </div>

          <h3 className="text-slate-900 font-black text-2xl tracking-tight mb-2">Clean Slate</h3>
          <p className="text-slate-500 text-sm font-medium leading-relaxed mb-8">
            No advertisements have been rejected. You're maintaining a great record! Keep up the professional approach.
          </p>
          <button
            onClick={fetchRejectedLeads}
            className="inline-flex items-center gap-2 bg-gradient-to-r from-rose-600 to-red-700 text-white px-8 py-3 rounded-2xl font-black text-sm uppercase tracking-widest hover:shadow-xl hover:shadow-rose-200 transition-all active:scale-95"
          >
            <FaRedo className={loading ? "animate-spin" : ""} />
            Check Updates
          </button>
        </div>
      )}

      {/* Rejected Leads List */}
      {!loading && !error && rejectedLeads.length > 0 && (
        <>
          {/* Mobile View - Compact Cards */}
          <div className="sm:hidden space-y-4">
            {paginatedLeads.map((lead, index) => {
              const hasImage = lead.imageUrl || lead.adDetails?.imageUrl;
              const hasVideo = lead.videoUrl || lead.adDetails?.videoUrl;
              const mediaUrl = lead.imageUrl || lead.adDetails?.imageUrl || lead.videoUrl || lead.adDetails?.videoUrl;

              return (
                <div key={lead._id || index} className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden flex h-32 transform transition-all active:scale-[0.98] ring-1 ring-slate-50">
                  {/* Left: Media Preview */}
                  <div className="w-28 bg-slate-50 flex-shrink-0 relative overflow-hidden group">
                    {mediaUrl ? (
                      <div className="w-full h-full">
                        {hasVideo ? (
                          <div className="relative w-full h-full bg-slate-900 flex items-center justify-center">
                            <FaAd className="text-white/20 text-xl" />
                            <div className="absolute inset-0 bg-black/20" />
                          </div>
                        ) : (
                          <img
                            src={mediaUrl.startsWith('http') ? mediaUrl : `${process.env.REACT_APP_API_BASE_URL}/${mediaUrl.replace(/\\/g, '/')}`}
                            alt="Ad"
                            className="w-full h-full object-cover"
                          />
                        )}
                      </div>
                    ) : (
                      <div className="w-full h-full flex items-center justify-center text-slate-300">
                        <FaAd className="text-2xl opacity-20" />
                      </div>
                    )}

                    {/* Badge Overlay */}
                    <div className="absolute top-2 left-2 flex flex-col gap-1">
                      <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase bg-red-600 text-white shadow-lg ring-1 ring-white/20">
                        REJECTED
                      </span>
                      <span className="px-2 py-0.5 rounded-md text-[8px] font-black uppercase bg-white/90 text-slate-900 shadow-sm backdrop-blur-sm border border-slate-100">
                        FIX NEEDED
                      </span>
                    </div>
                  </div>

                  {/* Right: Details Section */}
                  <div className="flex-1 p-3 flex flex-col justify-between min-w-0">
                    <div className="space-y-1.5">
                      <div className="flex justify-between items-start gap-1">
                        <h3 className="text-slate-900 font-black text-xs uppercase tracking-tight truncate leading-tight flex-1">
                          {lead.adType || "Advertisement"}
                        </h3>
                        <span className="text-red-600 font-bold text-[10px] whitespace-nowrap bg-red-50 px-2 py-0.5 rounded-full border border-red-100">
                          ₹{lead.finalReporterPrice || lead.totalCost || 0}
                        </span>
                      </div>

                      <div className="space-y-0.5">
                        <p className="text-slate-400 text-[8px] font-bold uppercase tracking-tight truncate font-mono bg-slate-50 px-1 rounded w-fit">
                          ID: {lead._id?.slice(-8).toUpperCase() || "N/A"}
                        </p>
                        <div className="flex items-center gap-1 mt-1 bg-red-50 p-1.5 rounded-lg border border-red-100/50">
                          <div className="w-1 h-3 bg-red-500 rounded-full" />
                          <p className="text-red-700 text-[9px] font-black uppercase tracking-tight truncate flex-1">
                            {lead.rejectNote || "No reason provided"}
                          </p>
                        </div>
                      </div>
                    </div>

                    <button
                      onClick={() => toast.error(`REJECTION REASON: ${lead.rejectNote || "No reason provided"}`, {
                        position: "top-center",
                        theme: "colored",
                        autoClose: 5000
                      })}
                      className="w-full py-2.5 bg-slate-900 hover:bg-slate-800 text-white text-[9px] font-black uppercase tracking-widest rounded-xl flex items-center justify-center gap-2 transition-all active:scale-95 shadow-md shadow-slate-200"
                    >
                      View Reason Details
                    </button>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="hidden sm:block overflow-x-auto">
            <table className="w-full bg-white border border-gray-200 rounded-lg overflow-hidden">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">ID</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Ad Type</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Budget</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rejection Reason</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Description</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {paginatedLeads.map((lead, index) => (
                  <tr key={index} className="hover:bg-gray-50 transition-colors">
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 max-w-[120px] truncate" title={lead._id}>
                        {lead._id || 'Not found'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                        {lead.adType || 'Not found'}
                      </span>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        ₹{lead.finalReporterPrice || lead.totalCost || 'Not found'}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">
                        {lead.startDate && lead.endDate
                          ? `${new Date(lead.startDate).toLocaleDateString()} to ${new Date(lead.endDate).toLocaleDateString()}`
                          : lead.startDate || lead.endDate || 'Not found'
                        }
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-red-100 text-red-800">
                        Rejected
                      </span>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 max-w-[200px] truncate" title={lead.rejectNote}>
                        {lead.rejectNote || 'No reason provided'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900 max-w-[200px] truncate" title={lead.mediaDescription}>
                        {lead.mediaDescription || 'Not found'}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Pagination Controls */}
            {totalPages > 1 && (
              <div className="flex justify-center mt-6">
                <nav className="inline-flex flex-wrap items-center gap-2">
                  <button
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === 1 ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-label="Previous page"
                  >
                    Previous
                  </button>
                  {/* Page number buttons */}
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map(pageNum => (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors font-semibold ${currentPage === pageNum ? 'bg-yellow-300 text-yellow-900' : ''}`}
                      aria-current={currentPage === pageNum ? 'page' : undefined}
                    >
                      {pageNum}
                    </button>
                  ))}
                  <button
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className={`px-4 py-1 rounded border border-yellow-400 text-yellow-800 bg-white hover:bg-yellow-100 transition-colors ${currentPage === totalPages ? 'opacity-50 cursor-not-allowed' : ''}`}
                    aria-label="Next page"
                  >
                    Next
                  </button>
                </nav>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default RejectedLeads;